<template>
	<div
		class="base_multiple_upload"
		:class="[showBorder ? 'border' : '', `column_${column}`]"
	>
		<div class="upload_area">
			<div
				v-for="(item, index) in fileList"
				:key="index"
				class="preview_item"
				:class="{ preview: preview }"
			>
				<img
					v-if="isImage(item)"
					:src="item.url || item.content"
					@click="clickPreview(item)"
				/>
				<video
					v-else-if="isVideo(item)"
					:src="item.url || item.content"
					@click="clickPreview(item)"
				></video>
				<div
					v-if="!disabled && !preview"
					class="delete_btn"
					@click="deleteFile(index)"
				>
					×
				</div>
				<div v-if="item.status === 'uploading'" class="upload_mask">
					<van-loading size="20px">{{
						item.message || "上传中"
					}}</van-loading>
				</div>
				<div v-if="item.status === 'failed'" class="failed_mask">
					{{ item.message || "上传失败" }}
				</div>
			</div>

			<div
				v-if="!disabled && !preview && fileList.length < listLimit"
				class="upload_btn"
				@click="triggerFileInput"
			>
				<van-icon name="plus" />
				<span>上传</span>
			</div>
		</div>

		<input
			ref="fileInput"
			type="file"
			:multiple="true"
			:accept="accept"
			style="display: none"
			@change="handleFileChange"
		/>

		<van-dialog
			v-model="previewShow.visible"
			title="预览"
			confirmButtonText="关闭"
		>
			<div style="padding: 20px 0">
				<video
					v-if="previewShow.type === 'video'"
					:src="previewShow.url"
					controls
					style="width: 100%; max-height: 300px"
				></video>
				<img
					v-else-if="previewShow.type === 'image'"
					:src="previewShow.url"
					style="width: 100%; max-height: 300px"
				/>
			</div>
		</van-dialog>
	</div>
</template>

<script>
import { uploadQiniu } from "@/utils/qiniu";

export default {
	name: "BaseMultipleUpload",
	data: () => ({
		previewShow: {
			visible: false,
			url: "",
			type: "",
		},
	}),
	props: {
		column: {
			type: [String, Number],
			default: 3,
		},
		value: {
			type: Array,
			default: () => [],
		},
		preview: {
			type: Boolean,
			default: false,
		},
		limit: {
			type: [Number, String],
			default: 5,
		},
		showBorder: {
			type: Boolean,
			default: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		accept: {
			type: String,
			default: "image/*",
		},
		repeatUpload: {
			type: Boolean,
			default: true,
		},
	},
	computed: {
		fileList: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit("input", val);
			},
		},
		listLimit() {
			if (this.preview) return this.value.length;
			return this.limit;
		},
	},
	methods: {
		triggerFileInput() {
			this.$refs.fileInput.click();
		},
		handleFileChange(event) {
			const files = Array.from(event.target.files);
			if (files.length === 0) return;

			// 检查文件数量限制
			if (this.fileList.length + files.length > this.limit) {
				this.$toast(`最多只能上传${this.limit}张图片`);
				return;
			}

			// 重置input，以便可以重复选择相同的文件
			event.target.value = "";

			// 验证文件
			if (!this.beforeRead(files)) {
				return;
			}

			// 处理文件
			this.processFiles(files);
		},
		processFiles(files) {
			const newFiles = files.map((file) => {
				// 创建预览URL
				let content = "";
				if (this.isImageFile(file)) {
					content = URL.createObjectURL(file);
				}

				return {
					file,
					content,
					url: "",
					status: "ready",
					message: "",
					originData: {
						name: file.name,
						size: file.size,
						type: file.type,
						lastModified: file.lastModified,
						lastModifiedDate: this.$moment(
							file.lastModifiedDate
						).format("YYYY-MM-DD HH:mm:ss"),
					},
				};
			});

			// 添加到文件列表
			this.fileList = [...this.fileList, ...newFiles];

			// 开始上传
			newFiles.forEach((file) => {
				this.processSingleFile(file);
			});
		},
		isImageFile(file) {
			return file.type.startsWith("image/");
		},
		isVideoFile(file) {
			return file.type.startsWith("video/");
		},
		isImage(item) {
			if (item.url) {
				return (
					!item.url.includes(".") ||
					["jpg", "jpeg", "png", "gif", "webp", "heic"].some((ext) =>
						item.url.toLowerCase().includes(`.${ext}`)
					)
				);
			}
			return item.content && item.content.startsWith("blob:");
		},
		isVideo(item) {
			if (item.url) {
				return ["mp4", "mov", "avi", "muv"].some((ext) =>
					item.url.toLowerCase().includes(`.${ext}`)
				);
			}
			return item.content && item.content.startsWith("blob:");
		},
		clickPreview(item) {
			this.previewShow.url = item.url || item.content;
			this.previewShow.type = this.isVideo(item) ? "video" : "image";
			this.previewShow.visible = true;
		},
		deleteFile(index) {
			this.fileList.splice(index, 1);
		},
		checkRepeatUpload(file) {
			if (!this.repeatUpload) {
				const isExist = this.fileList.some((item) => {
					if (item.originData) {
						if (
							item.originData.name === file.name &&
							item.originData.size === file.size
						)
							return true;
					}
					return false;
				});
				if (isExist) {
					this.$toast("请勿重复上传相同的图片");
					return false;
				}
			}
			return true;
		},
		beforeRead(files) {
			const imageAccept = [
				".jpg",
				".gif",
				".png",
				".webp",
				".heic",
				".jpeg",
			];
			const videoAccept = [
				".avi",
				".mp4",
				".mov",
				".muv",
				".AVI",
				".MP4",
				".MOV",
				".MUV",
			];

			let isValid = true;
			for (let i = 0; i < files.length; i++) {
				const file = files[i];

				if (this.accept.startsWith("image/")) {
					const imgType = file.name.substr(
						file.name.lastIndexOf(".")
					);
					if (!imageAccept.includes(imgType.toLowerCase())) {
						this.$toast(`请上传${imageAccept.join("、")}格式图片`);
						isValid = false;
						break;
					}
					if (file.size > 1024 * 1024 * 20) {
						this.$toast(`请上传20M以内的图片`);
						isValid = false;
						break;
					}
				} else if (this.accept.startsWith("video/")) {
					const itemSuffix = file.name.substr(
						file.name.lastIndexOf(".")
					);
					if (!videoAccept.includes(itemSuffix.toLowerCase())) {
						this.$toast(`请上传${videoAccept.join("、")}格式视频`);
						isValid = false;
						break;
					}
				} else if (this.accept === "*") {
					const imgType = file.name.substr(
						file.name.lastIndexOf(".")
					);
					if (
						![...imageAccept, ...videoAccept].includes(
							imgType.toLowerCase()
						)
					) {
						this.$toast(`上传文件格式不支持`);
						isValid = false;
						break;
					}
					if (
						imageAccept.includes(imgType.toLowerCase()) &&
						file.size > 1024 * 1024 * 20
					) {
						this.$toast(`请上传20M以内的图片`);
						isValid = false;
						break;
					}
				}

				if (!this.checkRepeatUpload(file)) {
					isValid = false;
					break;
				}
			}
			return isValid;
		},
		processSingleFile(fileItem) {
			fileItem.status = "uploading";
			fileItem.message = "上传中";

			this.$cos(fileItem.file, null, () => {})
				.then((url) => {
					fileItem.url = url;
					fileItem.status = "done";
					fileItem.message = "上传成功";

					// 释放预览URL
					if (fileItem.content) {
						URL.revokeObjectURL(fileItem.content);
						fileItem.content = "";
					}

					const list = this.fileList.filter(
						(file) => file.status === "done"
					);
					this.$emit("success", list, fileItem);
				})
				.catch((err) => {
					fileItem.status = "failed";
					fileItem.message = "上传失败";
				});
		},
	},
};
</script>

<style lang="less" scoped>
.base_multiple_upload {
	display: flex;
	flex-direction: column;

	&.border {
		border: 1px solid #eee;
		border-radius: 4px;
		padding: 10px;
	}

	.upload_area {
		display: flex;
		flex-wrap: wrap;
		margin: -5px;
	}

	.preview_item {
		position: relative;
		margin: 5px;
		border-radius: 4px;
		overflow: hidden;
		background-color: #f7f8fa;

		img,
		video {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		.delete_btn {
			position: absolute;
			top: 0;
			right: 0;
			width: 20px;
			height: 20px;
			background-color: rgba(0, 0, 0, 0.7);
			color: #fff;
			font-size: 16px;
			line-height: 20px;
			text-align: center;
			border-radius: 0 0 0 4px;
		}

		.upload_mask,
		.failed_mask {
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-color: rgba(0, 0, 0, 0.5);
			display: flex;
			align-items: center;
			justify-content: center;
			color: #fff;
			font-size: 12px;
		}

		.failed_mask {
			background-color: rgba(255, 0, 0, 0.5);
		}

		&.preview {
			.delete_btn {
				display: none;
			}
		}
	}

	.upload_btn {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		margin: 5px;
		background-color: #f7f8fa;
		color: #969799;
		font-size: 12px;
		cursor: pointer;

		.van-icon {
			font-size: 24px;
			margin-bottom: 5px;
		}
	}

	&.column_1 {
		.preview_item,
		.upload_btn {
			width: calc(100% - 10px);
			height: 100px;
		}
	}

	&.column_2 {
		.preview_item,
		.upload_btn {
			width: calc(50% - 10px);
			height: 100px;
		}
	}

	&.column_3 {
		.preview_item,
		.upload_btn {
			width: calc(33.33% - 10px);
			height: 100px;
		}
	}

	&.column_4 {
		.preview_item,
		.upload_btn {
			width: calc(25% - 10px);
			height: 100px;
		}
	}
}
</style>
